import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

/** Tailwind-aware className joiner used by every UI primitive. */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Collision-resistant enough for a local-first app, and readable in devtools. */
export function uid(prefix = 'id'): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
      : Math.random().toString(36).slice(2, 14)
  return `${prefix}_${rand}`
}

export function nowISO(): string {
  return new Date().toISOString()
}

/** Stamps `createdAt`/`updatedAt` on a freshly created entity. */
export function stamped<T extends object>(value: T): T & { createdAt: string; updatedAt: string } {
  const ts = nowISO()
  return { createdAt: ts, updatedAt: ts, ...(value as object) } as T & {
    createdAt: string
    updatedAt: string
  }
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function sortBy<T>(items: T[], selector: (item: T) => number | string): T[] {
  return [...items].sort((a, b) => {
    const av = selector(a)
    const bv = selector(b)
    if (av === bv) return 0
    return av < bv ? -1 : 1
  })
}

export function groupBy<T, K extends string>(items: T[], selector: (item: T) => K): Record<K, T[]> {
  return items.reduce((acc, item) => {
    const key = selector(item)
    ;(acc[key] ||= []).push(item)
    return acc
  }, {} as Record<K, T[]>)
}

/** Moves an array element, returning a new array — used by drag-and-drop. */
export function moveItem<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return items
  }
  const next = [...items]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

export function initials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
}
